/**
 * Unified Auth Context
 * Supports both Manus Auth and Firebase Auth based on environment
 * 
 * - Manus Dev (playxworld.dev): Uses Manus OAuth
 * - Firebase UAT (playxworld.net): Uses Firebase Auth
 */

import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { getAuthType, isMausAuth, isFirebaseAuth } from "@/lib/envDetection";
import { trpc } from "@/lib/trpc";
import {
  onFirebaseAuthStateChanged,
  firebaseSignInWithCustomToken,
  firebaseSignOut,
  getFirebaseIdToken,
  clearSessionCookie,
  type FirebaseUser,
} from "@/lib/firebase";

// Unified user profile
export interface UnifiedUser {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  profileImage: string | null;
  lineUserId: string | null;
  creditBalance: string;
  role: "user" | "admin";
  authType: "manus" | "firebase";
}

interface UnifiedAuthState {
  // User state
  user: UnifiedUser | null;
  loading: boolean;
  error: Error | null;

  // Auth state
  isAuthenticated: boolean;
  authType: "manus" | "firebase";

  // Actions
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const UnifiedAuthContext = createContext<UnifiedAuthState | null>(null);

export function UnifiedAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UnifiedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const authType = getAuthType();

  // Manus Auth: Use tRPC to get user
  const { data: manusUser, isLoading: manusLoading } = trpc.auth.me.useQuery(undefined, {
    enabled: isMausAuth(),
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Firebase Auth: Listen to Firebase auth state
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [firebaseLoading, setFirebaseLoading] = useState(isFirebaseAuth());

  useEffect(() => {
    if (!isFirebaseAuth()) {
      setFirebaseLoading(false);
      return;
    }

    // Set a timeout to ensure firebaseLoading is set to false even if Firebase fails
    const timeout = setTimeout(() => {
      setFirebaseLoading(false);
    }, 2000);

    const unsubscribe = onFirebaseAuthStateChanged((fbUser) => {
      clearTimeout(timeout);
      setFirebaseUser(fbUser);
      setFirebaseLoading(false);
    });

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, []);

  // Determine current user based on auth type
  useEffect(() => {
    if (isMausAuth()) {
      // Manus Auth flow
      setLoading(manusLoading);
      if (manusUser) {
        setUser({
          id: String(manusUser.id),
          name: manusUser.name || null,
          email: manusUser.email || null,
          phone: manusUser.phone || null,
          profileImage: manusUser.profileImage || null,
          lineUserId: manusUser.lineUserId || null,
          creditBalance: manusUser.creditBalance || "0",
          role: manusUser.role || "user",
          authType: "manus",
        });
      } else {
        setUser(null);
      }
    } else if (isFirebaseAuth()) {
      // Firebase Auth flow
      setLoading(firebaseLoading);
      if (firebaseUser) {
        // For Firebase, we need to fetch the PlayX user profile
        // This would be done via a tRPC call with the Firebase user's email
        setUser({
          id: firebaseUser.uid,
          name: firebaseUser.displayName || null,
          email: firebaseUser.email || null,
          phone: firebaseUser.phoneNumber || null,
          profileImage: firebaseUser.photoURL || null,
          lineUserId: null, // Would need to fetch from DB
          creditBalance: "0", // Would need to fetch from DB
          role: "user", // Would need to fetch from DB
          authType: "firebase",
        });
      } else {
        setUser(null);
      }
    }
  }, [manusUser, manusLoading, firebaseUser, firebaseLoading]);

  // Logout handler
  const logoutMutation = trpc.auth.logout.useMutation();
  const logout = useCallback(async () => {
    try {
      if (isMausAuth()) {
        // Manus logout
        await logoutMutation.mutateAsync();
      } else if (isFirebaseAuth()) {
        // Firebase logout
        await firebaseSignOut();
        // Clear session cookie
        await clearSessionCookie();
      }
      setUser(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Logout failed"));
      throw err;
    }
  }, [logoutMutation]);

  // Refresh handler
  const refresh = useCallback(async () => {
    try {
      if (isMausAuth()) {
        // Manus refresh via tRPC
        // The useQuery hook will automatically refetch
      } else if (isFirebaseAuth()) {
        // Firebase refresh
        if (firebaseUser) {
          await firebaseUser.reload();
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Refresh failed"));
      throw err;
    }
  }, [firebaseUser]);

  const value: UnifiedAuthState = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    authType,
    logout,
    refresh,
  };

  return (
    <UnifiedAuthContext.Provider value={value}>
      {children}
    </UnifiedAuthContext.Provider>
  );
}

export function useUnifiedAuth() {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error("useUnifiedAuth must be used within UnifiedAuthProvider");
  }
  return context;
}

// Hook to get auth type
export function useAuthType() {
  return getAuthType();
}

// Hook to check if using Manus Auth
export function useMausAuth() {
  return isMausAuth();
}

// Hook to check if using Firebase Auth
export function useFirebaseAuth() {
  return isFirebaseAuth();
}
